import { css } from '@emotion/core';
import { FontColorType } from '@weave/design-system';
import { theme } from '@weave/theme';
import {
  CustomizationFlagStatesV2,
  LocationFeatureV2,
} from 'models/location-feature.model';
import { VerticalEnum } from 'models/vertical-enum';
import {
  BundlePackagesEnum,
  CustomizationFlagsRequest,
  IntegrationPackagesEnum,
  SalesforceAccountProductBundle,
} from './customization-page-v2.types';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { INSURANCE_VERIFICATION_CF_LIST } from './customization-page-v2.constant';
import { AddOn } from '@weave/schema-gen-ts/dist/schemas/package-service/v1/package.pb';
import { FeatureEnumToNameMap } from 'constants/feature-data';
import { ToggleState } from 'components/shared/multi-state-toggle/multi-state-toggle.component';

export const getCustomizationFlagByBundlesRequest = (
  packageBundle: SalesforceAccountProductBundle,
  verticalId: VerticalEnum,
  addOns?: AddOn[]
): CustomizationFlagsRequest => {
  return {
    products: [packageBundle],
    vertical: verticalId,
    addons: addOns,
  };
};

export const getCustomizationFlagStateColor = (
  state: CustomizationFlagStatesV2
): FontColorType => {
  switch (state) {
    case CustomizationFlagStatesV2.ACTIVE:
      return 'success';
    case CustomizationFlagStatesV2.HIDE:
      return 'error';
    default:
      return 'default';
  }
};

export const getCustomizationFlagStateBackgroundColor = (
  state: CustomizationFlagStatesV2
) => {
  switch (state) {
    case CustomizationFlagStatesV2.ACTIVE:
      return css`
        background-color: ${theme.colors.success5};
      `;
    case CustomizationFlagStatesV2.HIDE:
      return css`
        background-color: ${theme.colors.critical10};
      `;
    case CustomizationFlagStatesV2.TRIAL:
      return css`
        background-color: ${theme.colors.warning5};
      `;
    case CustomizationFlagStatesV2.FREEMIUM:
      return css`
        background-color: ${theme.colors.secondary.eggplant10};
      `;
    default:
      return css`
        background-color: ${theme.colors.neutral10};
      `;
  }
};

export const getCustomizationFlagLabelByState = (state: CustomizationFlagStatesV2) => {
  switch (state) {
    case CustomizationFlagStatesV2.ACTIVE:
      return 'Show';
    case CustomizationFlagStatesV2.HIDE:
      return 'Hide';
    case CustomizationFlagStatesV2.TRIAL:
      return 'Trial';
    case CustomizationFlagStatesV2.FREEMIUM:
      return 'Freemium';
    case CustomizationFlagStatesV2.PROMOTE:
      return 'Promote';
    default:
      return 'Unknown';
  }
};

export const getIntegrationPackageName = (
  integrationPackage: IntegrationPackagesEnum
) => {
  switch (integrationPackage) {
    case IntegrationPackagesEnum.CORE:
      return IntegrationPackagesEnum.CORE;
    case IntegrationPackagesEnum.PLUS:
      return IntegrationPackagesEnum.PLUS;
  }
};

export const getBundlePackageName = (bundlePackage: BundlePackagesEnum) => {
  switch (bundlePackage) {
    case BundlePackagesEnum.ULTIMATE:
      return BundlePackagesEnum.ULTIMATE;
    case BundlePackagesEnum.ELITE:
      return BundlePackagesEnum.ELITE;
    case BundlePackagesEnum.ESSENTIALS:
      return BundlePackagesEnum.ESSENTIALS;
    case BundlePackagesEnum.PRO:
      return BundlePackagesEnum.PRO;
  }
};

export const SOFTWARE_ONLY = `SOFTWARE_ONLY`;

export const getProductPackageBundleName = (
  integrationPackage: IntegrationPackagesEnum,
  bundlePackage: BundlePackagesEnum,
  isSoftwareOnly?: boolean
): SalesforceAccountProductBundle | '' => {
  const productBundleBase = `PRODUCT_BUNDLE_WEAVE`;
  const productBundleIntegration = isSoftwareOnly
    ? `${integrationPackage}_${SOFTWARE_ONLY}`
    : integrationPackage;

  const productBundlePackage = bundlePackage;
  const productBundleName = `${productBundleBase}_${productBundleIntegration}_${productBundlePackage}`;

  const isProductBundleNameInSalesforceProductBundle = Object.values(
    SalesforceAccountProductBundle
  ).includes(productBundleName as SalesforceAccountProductBundle);

  if (isProductBundleNameInSalesforceProductBundle) {
    return productBundleName as SalesforceAccountProductBundle;
  }
  return '';
};

export const concatenateWithCommasAnd = (array: string[]) => {
  switch (array.length) {
    case 0:
      return '';
    case 1:
      return array[0];
    case 2:
      return `${array[0]} and ${array[1]}`;
    default:
      const lastElement = array.pop();
      const concatenatedString = array.join(', ');
      return `${concatenatedString} and ${lastElement}`;
  }
};

export const isQuotaEnabledForFeature = (feature: LocationFeatureV2) => {
  return [
    Feature.BULK_MESSAGING,
    Feature.EMAIL_MARKETING,
    Feature.TRUELARK_AI_VOICE,
    Feature.TRUELARK_AI_MESSAGING,
    Feature.WEAVE_VOICE_AIR,
    Feature.WEAVE_MESSAGING_AIR,
  ].includes(feature.featureEnum);
};

export const getQuotaTextByFeature = (featureEnum?: Feature) => {
  switch (featureEnum) {
    case Feature.EMAIL_MARKETING:
      return 'Emails';
    case Feature.TRUELARK_AI_VOICE:
    case Feature.WEAVE_VOICE_AIR:
      return 'Calls';
    default:
      return 'Messages';
  }
};

export const getQuotaAmountText = (
  quota: string | number,
  featureEnum?: Feature
): string => {
  if (!Number.isFinite(Number(quota))) {
    return `Unlimited ${getQuotaTextByFeature(featureEnum)}`;
  }
  return quota
    ? `${Number(quota).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} ${getQuotaTextByFeature(featureEnum)}`
    : '';
};

const INSURANCE_VERIFICATION_CF_NAMES = INSURANCE_VERIFICATION_CF_LIST.map(
  (featureEnum) => FeatureEnumToNameMap[featureEnum]
).filter((name): name is string => Boolean(name));

export const checkIsWeaveInsuranceVerificationFlag = (
  feature: Feature | Pick<LocationFeatureV2, 'featureEnum' | 'name'>
): boolean => {
  if (typeof feature === 'string') {
    return INSURANCE_VERIFICATION_CF_LIST.includes(feature);
  }

  const { featureEnum, name } = feature;
  return (
    (!!featureEnum && INSURANCE_VERIFICATION_CF_LIST.includes(featureEnum)) ||
    (!!name && INSURANCE_VERIFICATION_CF_NAMES.includes(name))
  );
};

// Resolves the display name used in the insurance verification disclaimer modals.
// Falls back to the flag name because the backend does not always populate
// featureEnum for the Enhanced Insurance Verification flag.
export const getInsuranceVerificationFeatureName = (
  feature: Pick<LocationFeatureV2, 'featureEnum' | 'name'>
): string => {
  const isEnhanced =
    feature.featureEnum === Feature.ENHANCED_INSURANCE_VERIFICATION ||
    feature.name === FeatureEnumToNameMap[Feature.ENHANCED_INSURANCE_VERIFICATION];

  return isEnhanced ? 'Weave Insurance Eligibility' : 'Weave Insurance Verification';
};

// Determines the transition details for a customization flag toggle.
// Compares the current and next state by their state key (not the display label)
// so that Show -> Hide correctly resolves to a hide transition.
export const getCustomizationFlagStateDetails = (
  currentState: CustomizationFlagStatesV2,
  newState: ToggleState
) => {
  const newStateKey = newState.key as CustomizationFlagStatesV2;
  const isHideState =
    newStateKey === CustomizationFlagStatesV2.HIDE &&
    currentState !== CustomizationFlagStatesV2.HIDE;
  const isFlagChanged = currentState !== newStateKey;

  return {
    isHideState,
    isFlagChanged,
  };
};

export enum BundleAddOnsEnum {
  BASE = 'BASE',
  COORDINATE = 'COORDINATE',
  ATTRACT = 'ATTRACT',
}

export const BundleAddOnsLabel: Record<BundleAddOnsEnum, string> = {
  [BundleAddOnsEnum.BASE]: 'Base',
  [BundleAddOnsEnum.COORDINATE]: 'Coordinate',
  [BundleAddOnsEnum.ATTRACT]: 'Attract',
};

const baseBundleMap: Record<string, SalesforceAccountProductBundle> = {
  [`${IntegrationPackagesEnum.CORE}_SO`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_BASE_NON_INTEGRATED,
  [`${IntegrationPackagesEnum.PLUS}_SO`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_BASE_INTEGRATED,
  [`${IntegrationPackagesEnum.CORE}_phones`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_BASE_PHONES,
  [`${IntegrationPackagesEnum.PLUS}_phones`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_BASE_PHONES_INTEGRATED,
};

const addOnMap: Record<string, AddOn> = {
  [`${BundleAddOnsEnum.COORDINATE}_${IntegrationPackagesEnum.PLUS}`]:
    AddOn.COORDINATE_INTEGRATED,
  [`${BundleAddOnsEnum.COORDINATE}_${IntegrationPackagesEnum.CORE}`]: AddOn.COORDINATE,
  [`${BundleAddOnsEnum.ATTRACT}_${IntegrationPackagesEnum.PLUS}`]:
    AddOn.ATTRACT_INTEGRATED,
  [`${BundleAddOnsEnum.ATTRACT}_${IntegrationPackagesEnum.CORE}`]: AddOn.ATTRACT,
};

export const bundleAddOnOptions = [
  BundleAddOnsEnum.BASE,
  BundleAddOnsEnum.COORDINATE,
  BundleAddOnsEnum.ATTRACT,
].map((value) => ({
  value,
  label: BundleAddOnsLabel[value],
}));

export const computeNewBundle = (
  integration: IntegrationPackagesEnum,
  phones?: boolean
): SalesforceAccountProductBundle => {
  const key = `${integration}_${phones ? 'phones' : 'SO'}`;
  return baseBundleMap[key];
};

export const computeNewBundleAddOns = (
  integration: IntegrationPackagesEnum,
  selectedAddOns: BundleAddOnsEnum[]
): AddOn[] => {
  return selectedAddOns
    .filter((addon) => addon !== BundleAddOnsEnum.BASE)
    .map((addon) => addOnMap[`${addon}_${integration}`]);
};

export const BundlePackagesLabel: Record<BundlePackagesEnum, string> = {
  [BundlePackagesEnum.ULTIMATE]: 'Ultimate',
  [BundlePackagesEnum.ELITE]: 'Elite',
  [BundlePackagesEnum.PRO]: 'Pro',
  [BundlePackagesEnum.ESSENTIALS]: 'Essentials',
};

const oldBundleMap: Record<string, SalesforceAccountProductBundle> = {
  [`${BundlePackagesEnum.ULTIMATE}_${IntegrationPackagesEnum.PLUS}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_PLUS_ULTIMATE,
  [`${BundlePackagesEnum.ULTIMATE}_${IntegrationPackagesEnum.CORE}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_CORE_ULTIMATE,
  [`${BundlePackagesEnum.ELITE}_${IntegrationPackagesEnum.PLUS}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_PLUS_ELITE,
  [`${BundlePackagesEnum.ELITE}_${IntegrationPackagesEnum.CORE}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_CORE_ELITE,
  [`${BundlePackagesEnum.PRO}_${IntegrationPackagesEnum.PLUS}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_PLUS_PRO,
  [`${BundlePackagesEnum.PRO}_${IntegrationPackagesEnum.CORE}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_CORE_PRO,
  [`${BundlePackagesEnum.ESSENTIALS}_${IntegrationPackagesEnum.PLUS}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_PLUS_ESSENTIALS,
  [`${BundlePackagesEnum.ESSENTIALS}_${IntegrationPackagesEnum.CORE}`]:
    SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_CORE_ESSENTIALS,
};

export const bundlePackageOptions = Object.values(BundlePackagesEnum).map((value) => ({
  value,
  label: BundlePackagesLabel[value],
}));

export const computeOldBundle = (
  integration: IntegrationPackagesEnum,
  bundlePackage: BundlePackagesEnum
): SalesforceAccountProductBundle => {
  const key = `${bundlePackage}_${integration}`;
  return oldBundleMap[key];
};
